import usePreviousEffect from "@pathwright/ui/src/components/hooks/usePreviousEffect"
import { useEffect, useMemo, useRef, useState } from "react"
import { useReviewContext } from "./ReviewContextProvider"
import { TreeNodeAny, getLeafNodes } from "./tree/path-tree"

type OrderedTreeNode = TreeNodeAny & { index: number }

const getOrderedItemStack = (nodes: TreeNodeAny[]) => {
  return nodes.map((node, index) => {
    ;(node as OrderedTreeNode).index = index
    return node
  }) as OrderedTreeNode[]
}

const useReviewStack = () => {
  // this uses the review context selected and selected parent to build a stack of completions
  // that can be reviewed and navigated through
  const {
    root,
    selected,
    selectedParent,
    context,
    actions: { select, getItem }
  } = useReviewContext()

  // this is weird... selectedParent is stale here so I have to get it directly from the store?
  // it seems the staleness happens when a server side context filter occurs
  const parent = root ? getItem(root.id) : null
  // To retain previous behavior, we'll scope nav to the root, so we navigate all
  // items that are listed in the main view. The x/y indicator maps to x = current index
  // and y = total number of items anyhow, so we'd have to alter that if we wanted to
  // really scope to the selectedParent.
  // const parent = selectedParent ? getItem(selectedParent.id) : null

  // the stack of completions that are selected
  const [items, setItems] = useState<OrderedTreeNode[]>(() =>
    parent ? getOrderedItemStack(getLeafNodes(parent)) : []
  )

  // Those items which can be navigated, which includes all items, just reorganized
  // so that the all "newer" items appearing at earlier leaf node indexes will
  // be moved to after the currently selected item. This makes pagination a little less awkward.
  const [navigableItems, setNavigableItems] = useState(items)

  // If after paginating, we are awaiting a `next` selection, then
  // pendingNextRef.current will be true.
  const pendingNextRef = useRef(false)

  // Handle setting the navigableItems. Comparing the previous items with the current
  // to determine which new items to the list should get moved to appear after the
  // currently selected item.
  usePreviousEffect(
    ([prevItems]: [OrderedTreeNode[]]) => {
      const hasNew = items.find(
        (item) => !prevItems.find((prevItem) => prevItem.id === item.id)
      )
      const selectedIndex = selected
        ? prevItems.findIndex((item) => item.id === selected.id)
        : -1
      if (hasNew && selectedIndex !== -1) {
        const startingItems = prevItems.filter((item, i) => i <= selectedIndex)
        const endingItems = items.filter(
          (item) =>
            !startingItems.find((startingItem) => startingItem.id === item.id)
        )
        const nextItems = [...startingItems, ...endingItems]
        setNavigableItems(nextItems)
      } else if (items.length !== navigableItems.length) {
        setNavigableItems(items)
      }
    },
    [items]
  )

  useEffect(() => {
    if (parent) {
      const nextStack = getOrderedItemStack(getLeafNodes(parent))
      setItems(nextStack)
    }
  }, [parent])

  // a horrible, no good, terrible hack to set the the index of the selected item in the stack
  const selectedIndex = selected
    ? navigableItems.findIndex((item) => item.id === selected.id)
    : -1

  // Sure hope we aren't depending on this mutation!
  // if (selected) selected.index = selectedIndex

  const selectedIndexedItem: OrderedTreeNode | null = useMemo(() => {
    return selected ? { ...selected, index: selectedIndex } : null
  }, [selectedIndex, selected])

  const isSingleItemMode = !!context.query && !!context.query.step_id
  const hasNext = selected && selectedIndex < (context.total || 0) - 1
  const hasPrevious = selected && selectedIndex > 0

  const previous = hasPrevious
    ? () => select(navigableItems[selectedIndex - 1])
    : undefined

  const next = hasNext
    ? () => {
        const { hasMore, loadingMore, loadMore } = context
        const next = navigableItems[selectedIndex + 1]

        if (!next && hasMore && !loadingMore) {
          pendingNextRef.current = true
          loadMore()
        }

        if (next) {
          pendingNextRef.current = false
          select(next)
        }
      }
    : undefined

  // Select next pending item.
  useEffect(() => {
    if (pendingNextRef.current) {
      next?.()
    }
  }, [navigableItems.length])

  return {
    items: navigableItems,
    selected: selectedIndexedItem,
    hasNext,
    hasPrevious,
    isSingleItemMode,
    actions: {
      previous,
      next
    }
  }
}

export default useReviewStack
